import React from 'react';
import useReveal from '../hooks/useReveal';
import './Story.css';

const pillars = [
  {
    icon: '🌾',
    title: 'Desi Seeds',
    desc: 'Native heirloom seeds preserved for generations, grown without genetic modification or lab interference.',
  },
  {
    icon: '🐄',
    title: 'Desi Cow',
    desc: 'Indigenous cows provide natural manure and bio-inputs that enrich the soil and nourish every crop.',
  },
  {
    icon: '🌿',
    title: 'Neem Tree',
    desc: 'Nature\'s own pesticide — neem-based solutions replace all synthetic chemicals on our farm.',
  },
  {
    icon: '🧑‍🌾',
    title: 'ZBNF & NFM',
    desc: 'Guided by Subhash Palekar\'s Zero Budget Natural Farming and Natural Farming Marketplace principles.',
  },
];

const crises = [
  {
    stat: '50%',
    label: 'Farmers predicted to leave agriculture by 2050',
    source: 'United Nations',
    icon: '📉',
  },
  {
    stat: '2050',
    label: 'Projected year of significant global food shortage',
    source: 'UN Food Forecast',
    icon: '🍽️',
  },
  {
    stat: '2070',
    label: 'Degraded soil may yield nothing, if chemical use continues',
    source: 'UN Scientists',
    icon: '🌍',
  },
];

export default function Story() {
  const ref = useReveal();

  return (
    <section className="story-section" id="story" ref={ref}>
      {/* Decorative blobs */}
      <div className="story-blob story-blob-1" />
      <div className="story-blob story-blob-2" />

      {/* ── Header ── */}
      <div className="section-header reveal">
        <h2>Our Story</h2>
        <p>Why we farm the way we do — and why it matters for everyone.</p>
      </div>

      {/* ── Crisis cards ── */}
      <div className="story-crisis reveal">
        <p className="crisis-intro">
          Farming is in crisis. The numbers tell a story that we can no longer ignore.
        </p>
        <div className="crisis-grid">
          {crises.map(({ stat, label, source, icon }) => (
            <div className="crisis-card" key={stat}>
              <span className="crisis-icon">{icon}</span>
              <span className="crisis-stat">{stat}</span>
              <p className="crisis-label">{label}</p>
              <span className="crisis-source">{source}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Two-column narrative ── */}
      <div className="story-narrative">
        {/* Left — the problem */}
        <div className="story-block reveal-left">
          <div className="story-block-icon">⚠️</div>
          <h3>The Problem</h3>
          <p>
            Over the last two decades, farming has become an unwanted profession — even
            by farmers themselves. Lack of profit, insufficient government support, and
            the attraction to urban life have pushed millions of farmers away from agriculture.
          </p>
          <p>
            Worse, the widespread use of lab-grown seeds, chemical fertilizers, and
            synthetic pesticides has steadily degraded the soil. These chemicals have
            also introduced life-threatening chronic diseases in humans from an
            increasingly early age, while accelerating pollution in air and water.
          </p>
          <p>
            If nothing changes, UN scientists warn that degraded soil may yield nothing
            at all by <strong>2070</strong> — leaving future generations with no means
            to grow food.
          </p>
        </div>

        {/* Right — our response */}
        <div className="story-block reveal-right">
          <div className="story-block-icon">🌱</div>
          <h3>Our Solution</h3>
          <p>
            Adiyogi Natural Farms was created with a clear purpose: produce
            chemical-free organic food for people living today, while preserving and
            enriching the soil for generations yet to come.
          </p>
          <p>
            We achieve this by working with nature rather than against it — leveraging
            natural ecosystems, native resources, and time-tested methods guided by
            <strong> Subhash Palekar's Zero Budget Natural Farming (ZBNF)</strong> and
            the principles of the <strong>Natural Farming Marketplace (NFM)</strong>.
          </p>
          <p>
            Every crop we grow is a small act of resistance against degradation and a
            quiet promise to the future.
          </p>
        </div>
      </div>

      {/* ── Four pillars ── */}
      <div className="story-pillars-wrap reveal">
        <h3 className="pillars-heading">Built on Four Natural Pillars</h3>
        <div className="story-pillars">
          {pillars.map(({ icon, title, desc }, i) => (
            <div
              className="pillar-card reveal"
              key={title}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              <span className="pillar-icon">{icon}</span>
              <h4>{title}</h4>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Closing quote ── */}
      <div className="story-quote reveal">
        <blockquote>
          "We don't just grow food. We grow hope — for the present, and for the earth
          our children will inherit."
        </blockquote>
        <span className="story-quote-attr">— Adiyogi Natural Farms</span>
      </div>
    </section>
  );
}
